'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { Room, RoomEvent } from 'livekit-client';
import { motion } from 'motion/react';
import { RoomAudioRenderer, RoomContext, StartAudio, } from '@livekit/components-react';
import { useJoinLiveKitMutation } from '@/Adminhooks/mutation/chat';
import { useChatOnly } from '@/app/context/useChatOnly';
import { useMicPermission } from '@/app/context/useMicrophone';
import { useModal } from '@/app/context/useModal';
import { useMode } from '@/app/context/useMode';
import { ErrorMessage } from '@/components/embed-popup/error-message';
import { PopupView } from '@/components/embed-popup/popup-view';
import useConnectionDetails from '@/hooks/use-connection-details';
import { useGlobalSilence } from '@/hooks/useGlobalSilent';
import { RobotBottomBar, RobotProvider } from '@/plugin/src';
import Confirmation from '../confirmation/confirmation';
import { AgentUI } from './agent-ui';
import MicPermissionGate from './micPermissionModal';
import { RobotWrapper } from './robotStates/robotGreetings';
import { Trigger } from './trigger';
const PopupViewMotion = motion.create(PopupView);
function AgentClient({ appConfig, setWidgetControll, assetsBasePath = '', }) {
    const isAnimating = useRef(false);
    const room = useMemo(() => new Room(), []);
    const [popupOpen, setPopupOpen] = useState(false);
    const [close, isClose] = useState(true);
    const [error, setError] = useState(null);
    const { mode, setMode } = useMode();
    const { isModalOpen, openModal, closeModal } = useModal();
    const [showInactivityModal, setShowInactivityModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const { enableChatOnly, isChatOnly, disableChatOnly } = useChatOnly();
    const { connectionDetails, refreshConnectionDetails, existingOrRefreshConnectionDetails } = useConnectionDetails(appConfig, assetsBasePath);
    const { micPermission } = useMicPermission();
    const { isSpeakerMuted, setIsSpeakerMuted } = useGlobalSilence(micPermission === 'denied'
        ? true
        : typeof window !== 'undefined' && sessionStorage.getItem('chat') === 'true'
            ? true
            : false);
    const joinLiveKit = useJoinLiveKitMutation();
    useEffect(() => {
        if (joinLiveKit.status === 'error') {
            setShowErrorModal(true);
            setError({
                title: '403 – Access Denied',
                description: 'You do not have permission to access the requested file, directory, or website.',
            });
            try {
                room.disconnect();
            }
            catch (_a) { }
        }
    }, [joinLiveKit.status, room]);
    useEffect(() => {
        joinLiveKit.mutate();
    }, []);
    useEffect(() => {
        if (micPermission === 'denied' && sessionStorage.getItem('hideWidget')) {
            setIsSpeakerMuted(true);
            sessionStorage.setItem('chat', 'true');
            sessionStorage.setItem('agentOpen', 'true');
            setPopupOpen(true);
            setMode('inline');
        }
    }, [micPermission]);
    useEffect(() => {
        Cookies.set('agentOpen', popupOpen ? 'true' : 'false', {
            path: '/',
            expires: 1,
            sameSite: 'none',
            secure: true,
        });
    }, [popupOpen]);
    const handleTogglePopup = () => {
        if (isAnimating.current)
            return;
        setError(null);
        setPopupOpen((open) => !open);
    };
    const handlePanelAnimationStart = () => {
        isAnimating.current = true;
    };
    const handlePanelAnimationComplete = () => {
        isAnimating.current = false;
        if (!popupOpen && room.state !== 'disconnected') {
            room.disconnect();
        }
    };
    useEffect(() => {
        const onDisconnected = () => {
            setPopupOpen(false);
            refreshConnectionDetails();
        };
        const onMediaDevicesError = (error) => {
            setError({
                title: 'Encountered an error with your media devices',
                description: `${error.name}: ${error.message}`,
            });
        };
        room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);
        room.on(RoomEvent.Disconnected, onDisconnected);
        // room.off(RoomEvent.Disconnected, onDisconnected);
        room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
        return () => {
            room.off(RoomEvent.Disconnected, onDisconnected);
            room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
        };
    }, [room, refreshConnectionDetails]);
    useEffect(() => {
        if (!popupOpen || !connectionDetails || room.state !== 'disconnected')
            return;
        let cancelled = false;
        const connect = async () => {
            try {
                const details = connectionDetails !== null && connectionDetails !== void 0 ? connectionDetails : (await existingOrRefreshConnectionDetails());
                await room.connect(details.serverUrl, details.participantToken);
                if (cancelled)
                    return;
                if (micPermission === 'granted') {
                    await room.localParticipant.setMicrophoneEnabled(true, undefined, {
                        preConnectBuffer: appConfig.isPreConnectBufferEnabled,
                    });
                }
            }
            catch (err) {
                if (err instanceof Error && !cancelled) {
                    setShowErrorModal(true);
                    setError({
                        title: 'There was an error connecting to the agent',
                        description: `${err.name}: ${err.message}`,
                    });
                }
            }
        };
        connect();
        return () => {
            cancelled = true;
        };
    }, [
        popupOpen,
        connectionDetails,
        existingOrRefreshConnectionDetails,
        appConfig.isPreConnectBufferEnabled,
        room,
        micPermission,
    ]);
    const [loading, setLoading] = useState(true);
    const [agentIsReady, setAgentIsReady] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => {
            setLoading(false);
        }, 500);
        return () => clearTimeout(timer);
    }, []);
    const [enterAnim, setEnterAnim] = useState(false);
    useEffect(() => {
        if (!loading)
            setEnterAnim(true);
    }, [loading]);
    useEffect(() => {
        if (room.state === 'disconnected' && sessionStorage.getItem('hideWidget') === 'true') {
            setMode('bottom-bar');
        }
    }, [loading]);
    useEffect(() => {
        if (room.state === 'connected') {
            sessionStorage.setItem('hideWidget', 'true');
        }
    }, [room.state, loading, agentIsReady]);
    useEffect(() => {
        if (micPermission)
            closeModal();
    }, [micPermission]);
    useEffect(() => {
        if ((micPermission === 'granted' && isModalOpen) || isChatOnly) {
            setPopupOpen(true);
            setMode('inline');
            sessionStorage.setItem('agentOpen', 'true');
        }
    }, [micPermission, isModalOpen, isChatOnly]);
    useEffect(() => {
        if (sessionStorage.getItem('chat') === 'true') {
            sessionStorage.setItem('chat', 'true');
        }
        else {
            sessionStorage.setItem('chat', 'false');
        }
    }, []);
    useEffect(() => {
        if (sessionStorage.getItem('agentOpen') === 'true' ||
            sessionStorage.getItem('tessOpen') === 'true') {
            setPopupOpen(true);
            setMode('inline');
            sessionStorage.setItem('noPermission', 'false');
            sessionStorage.setItem('tessOpen', 'false');
        }
    }, []);
    const handleCloseAction = () => {
        setPopupOpen(false);
        sessionStorage.setItem('hideWidget', 'true');
        setWidgetControll && setWidgetControll(true);
        disableChatOnly();
        sessionStorage.setItem('agentOpen', 'false');
        sessionStorage.setItem('micMute', 'false');
        sessionStorage.setItem('chat', 'false');
        sessionStorage.setItem('isSpeakerMuted', 'false');
        setMode('bottom-bar');
    };
    return (_jsxs(_Fragment, { children: [joinLiveKit.status !== 'error' && (_jsxs(_Fragment, { children: [_jsxs(RoomContext.Provider, { value: room, children: [_jsx(RoomAudioRenderer, {}), _jsx(StartAudio, { label: "" }), _jsx(AgentUI, { setAgentIsReady: setAgentIsReady }), !loading && (_jsxs(_Fragment, { children: [!agentIsReady &&
                                        sessionStorage.getItem('hideWidget') !== 'true' &&
                                        (room.state === 'disconnected' || room.state === 'connecting') && (_jsx(motion.div, { initial: { opacity: 0, y: 100 }, animate: {
                                            opacity: enterAnim ? 1 : 0,
                                            y: enterAnim ? 0 : 100,
                                        }, transition: {
                                            type: 'spring',
                                            bounce: 0.3,
                                            duration: 0.8,
                                        }, className: "fixed bottom-0 left-0 z-50 flex w-full justify-center", children: _jsx(Trigger, { error: error, popupOpen: popupOpen, onToggle: handleTogglePopup, isClose: isClose, setWidgetControll: setWidgetControll }) })), !(agentIsReady && sessionStorage.getItem('agentClicked') === 'true') &&
                                        ((room.state !== 'disconnected' &&
                                            sessionStorage.getItem('hideWidget') === 'true') ||
                                            room.state === 'connected') && (_jsx(motion.div, { inert: !popupOpen, initial: { opacity: 0, translateY: 200 }, animate: {
                                            opacity: enterAnim ? (popupOpen ? 1 : 0) : 0,
                                            translateY: enterAnim ? (popupOpen ? 0 : 200) : 200,
                                        }, transition: {
                                            type: 'spring',
                                            bounce: 0.25,
                                            duration: 0.8,
                                        }, onAnimationStart: handlePanelAnimationStart, onAnimationComplete: handlePanelAnimationComplete, className: "fixed bottom-0 left-0 z-50 flex w-full justify-center", children: _jsx("div", { className: "xs:pl-0 h-[480px] w-full overflow-y-auto pl-2", style: {
                                                backgroundImage: 'linear-gradient(to top, black 40%, transparent)',
                                                filter: 'drop-shadow(0 4px 3px rgb(0 0 0 / 0.07)) drop-shadow(0 2px 2px rgb(0 0 0 / 0.06))',
                                            }, children: _jsxs("div", { className: "relative h-full w-full", children: [_jsx(ErrorMessage, { error: error }), !error && (_jsx(PopupViewMotion, { appConfig: appConfig, initial: { opacity: 1 }, animate: {
                                                            opacity: error === null ? 1 : 0,
                                                            transition: {
                                                                duration: 0.2,
                                                                ease: 'linear',
                                                            },
                                                        }, disabled: !popupOpen, sessionStarted: popupOpen, onEmbedError: setError, className: "absolute inset-0", setPopupOpen: setPopupOpen, setAgentIsReady: setAgentIsReady, setWidgetControll: setWidgetControll, loading: agentIsReady, setShowInactivityModal: setShowInactivityModal, showInactivity: showInactivityModal, assetsBasePath: assetsBasePath }))] }) }) }))] }))] }), !loading && agentIsReady && sessionStorage.getItem('agentClicked') === 'true' && (_jsx("div", { className: "fixed bottom-0 left-0 z-50 flex w-full justify-center", children: _jsx(RobotProvider, { mode: mode, targetElementId: "robot-container", assetsBasePath: `${assetsBasePath}/robot-assets`, children: _jsx(RobotWrapper, {}) }) })), mode === 'bottom-bar' && (_jsx(RobotProvider, { mode: mode, assetsBasePath: `${assetsBasePath}/robot-assets`, onRobotClick: () => {
                            if (micPermission === 'granted') {
                                setPopupOpen(true);
                                sessionStorage.setItem('chat', 'false');
                                setMode('inline');
                                sessionStorage.setItem('agentOpen', 'true');
                            }
                            sessionStorage.setItem('agentOpen', 'true');
                            sessionStorage.setItem('agentClicked', 'true');
                            openModal();
                        }, children: _jsx(RobotBottomBar, {}) })), isModalOpen && (micPermission === 'prompt' || micPermission === 'denied') && (_jsx(MicPermissionGate, {}))] })), showErrorModal && error && (_jsx("div", { className: "fixed inset-0 z-[9999999] flex items-center justify-center", children: _jsxs("div", { className: "relative w-full max-w-md rounded-2xl border border-white/40 bg-[#FFFFFF] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.15)] backdrop-blur-xl backdrop-saturate-150", children: [_jsx("div", { className: "pointer-events-none absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-white/40 to-transparent opacity-60 blur-xl" }), _jsx("button", { onClick: () => setShowErrorModal(false), className: "absolute top-3 right-3 cursor-pointer rounded-full bg-white/40 px-2 py-1 text-gray-600 backdrop-blur-md transition hover:bg-white/60", "aria-label": "Close", children: "\u2715" }), _jsx("h3", { className: "text-lg font-semibold tracking-tight text-gray-900", children: error.title }), _jsx("p", { className: "word-break mt-2 text-sm leading-relaxed text-gray-700/80", children: error.description }), joinLiveKit.status !== 'error' && (_jsx("button", { onClick: () => window.location.reload(), className: "mt-6 w-full cursor-pointer rounded-xl bg-blue-600/90 px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-blue-600/30 backdrop-blur-md transition hover:bg-blue-700/90", children: "Refresh page" }))] }) })), showInactivityModal && (_jsx(Confirmation, { setClose: setShowInactivityModal, handleClose: handleCloseAction, showInactivity: showInactivityModal }))] }));
}
export default AgentClient;
